import PropTypes from "prop-types";
import { Container, Text, Badge } from "@artprov/design-system";
import MatchingCard from "../../matching-bancario/detalle/MatchingCard";
import DocumentoCard from "../../matching-bancario/detalle/DocumentoCard";
import HistoricoEmbargosCard from "./HistoricoEmbargosCard";
import JudicialDetailCard from "./JudicialDetailCard";
import { GrupoCell } from "../components/GrupoCell";
import { getBadgeVariantByPercentage } from "../../matching-bancario/detalle/utils/badgeUtils";
import { formatCurrency } from "../../../common/utils/functions/formatCurrency";

const MatchingJudicialDetail = ({
    matchingData,
    emailBancario,
    extractoBancario,
    juicioAsociado,
    documento,
    historicoEmbargos,
    idEmail,
}) => {
    const { variant, label, percentageValue, darkerBgClass } =
        getBadgeVariantByPercentage(matchingData?.porcentajeTotal);

    // Campos de Extracto Bancario
    const extractoBancarioFields = [
        { label: "Banco", value: extractoBancario?.bancoDescripcion, fullWidth: false },
        { label: "Número de cuenta", value: extractoBancario?.cuentaNumero, fullWidth: false },
        { label: "Importe", value: formatCurrency(extractoBancario?.importe), fullWidth: false },
        { label: "Fecha de movimiento", value: extractoBancario?.fechaMovimiento, fullWidth: false },
    ];

    // Campos de Detalle del Juicio
    const juicioFields = [
        {
            label: "Carpeta",
            value: juicioAsociado?.carpeta,
            fullWidth: false,
            valueNode: juicioAsociado?.carpeta ? (
                <Badge variant="filled" type="soft" state="information">
                    {juicioAsociado.carpeta}
                </Badge>
            ) : null,
        },
        {
            label: "Tipo de juicio",
            value: juicioAsociado?.tipoJuicio,
            fullWidth: false,
            valueNode: juicioAsociado?.tipoJuicio ? (
                <Badge variant="filled" type="soft" state="neutral">
                    {juicioAsociado.tipoJuicio}
                </Badge>
            ) : null,
        },
        { label: "N° de expediente", value: juicioAsociado?.expediente, fullWidth: false },
        { label: "CUIJ", value: juicioAsociado?.cuij, fullWidth: false },
        { label: "Demandante", value: juicioAsociado?.actor, fullWidth: false },
        { label: "Demandado", value: juicioAsociado?.demandado, fullWidth: false },
        { label: "Jurisdicción", value: juicioAsociado?.jurisdiccion, fullWidth: false },
        { label: "Fuero", value: juicioAsociado?.fuero, fullWidth: false },
        { label: "Juzgado", value: juicioAsociado?.juzgado, fullWidth: false },
        { label: "Secretaría", value: juicioAsociado?.secretaria, fullWidth: false },
    ];

    // Campos de Detalle del Embargo
    const embargoFields = [
        { label: "Carátula", value: emailBancario?.caratula, fullWidth: true },
        { label: "Embargo", value: emailBancario?.nroEmbargo, fullWidth: true },
        { label: "Demandante", value: emailBancario?.actor, fullWidth: false },
        { label: "Demandado", value: emailBancario?.demandado, fullWidth: false },
        { label: "Expediente", value: emailBancario?.expediente, fullWidth: false },
        { label: "Oficio", value: emailBancario?.oficio, fullWidth: false },
        { label: "Jurisdicción", value: emailBancario?.jurisdiccion, fullWidth: false },
        { label: "Fuero", value: emailBancario?.fuero, fullWidth: false },
        { label: "Juzgado", value: emailBancario?.juzgado, fullWidth: false },
        { label: "Secretaría", value: emailBancario?.secretaria, fullWidth: false },
        { label: "Observaciones", value: emailBancario?.observaciones, fullWidth: true },
    ];

    // Campos del documento
    const documentoFields = [
        { label: "Remitente", value: emailBancario?.remitenteMail },
        { label: "Asunto", value: emailBancario?.asuntoMail },
    ];

    return (
        <Container className="bg-tertiary-98">
            <div className="flex flex-col gap-2">
                {/* Header con Carátula y Badges */}
                <div className="flex items-start justify-between mb-3">
                    <div className="flex flex-col gap-3">
                        <Text variant="h6" className="text-primary-10 mb-1">
                            {emailBancario?.caratula || "-"}
                        </Text>
                        <div className="flex items-center gap-2">
                            <Badge variant={variant} className="font-semibold flex items-center gap-1">
                                {label}
                                <span className={`${darkerBgClass} text-tertiary-100 px-1.5 py-0.5 rounded-full`}>
                                    <span className="font-medium">{percentageValue}</span>
                                </span>
                            </Badge>
                            <GrupoCell value={juicioAsociado?.grupo} showFullText={true} />
                        </div>
                    </div>
                </div>

                {/* Grid de 3 columnas */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Columna 1 */}
                    <div className="flex flex-col gap-4">
                        <MatchingCard matchingData={matchingData} />
                        <JudicialDetailCard
                            title="Información de extracto bancario"
                            fields={extractoBancarioFields}
                        />
                    </div>

                    {/* Columna 2 */}
                    <div>
                        <JudicialDetailCard
                            title="Detalle del juicio"
                            fields={juicioFields}
                            titleRightContent={
                                <Badge variant="filled" type="outline" state="neutral">
                                    Coincidencia judicial
                                </Badge>
                            }
                        />
                    </div>

                    {/* Columna 3 */}
                    <div>
                        <JudicialDetailCard
                            title="Detalle del embargo"
                            fields={embargoFields}
                        />
                    </div>
                </div>

                {/* Card de histórico de embargos - ancho completo */}
                {historicoEmbargos?.length > 0 && (
                    <div className="mt-4">
                        <HistoricoEmbargosCard data={historicoEmbargos} />
                    </div>
                )}

                {/* Card de documento - ancho completo */}
                <div className="mt-4">
                    <DocumentoCard
                        embargoId={idEmail?.toString()}
                        documentoMetadata={documento}
                        fields={documentoFields}
                    />
                </div>
            </div>
        </Container>
    );
};

MatchingJudicialDetail.propTypes = {
    matchingData: PropTypes.shape({
        porcentajeTotal: PropTypes.number,
        campos: PropTypes.arrayOf(
            PropTypes.shape({
                campo: PropTypes.string,
                porcentaje: PropTypes.number,
            })
        ),
    }),
    emailBancario: PropTypes.shape({
        id: PropTypes.number,
        expediente: PropTypes.string,
        oficio: PropTypes.string,
        jurisdiccion: PropTypes.string,
        fuero: PropTypes.string,
        juzgado: PropTypes.string,
        secretaria: PropTypes.string,
        caratula: PropTypes.string,
        actor: PropTypes.string,
        demandado: PropTypes.string,
        observaciones: PropTypes.string,
        remitenteMail: PropTypes.string,
        asuntoMail: PropTypes.string,
        nroEmbargo: PropTypes.string,
    }),
    extractoBancario: PropTypes.shape({
        id: PropTypes.number,
        bancoId: PropTypes.number,
        bancoDescripcion: PropTypes.string,
        cuentaId: PropTypes.number,
        cuentaNumero: PropTypes.string,
        fechaMovimiento: PropTypes.string,
        importe: PropTypes.number,
    }),
    juicioAsociado: PropTypes.shape({
        id: PropTypes.number,
        carpeta: PropTypes.string,
        tipoJuicio: PropTypes.string,
        grupo: PropTypes.string,
        expediente: PropTypes.string,
        cuij: PropTypes.string,
        actor: PropTypes.string,
        demandado: PropTypes.string,
        jurisdiccion: PropTypes.string,
        fuero: PropTypes.string,
        juzgado: PropTypes.string,
        secretaria: PropTypes.string,
    }),
    documento: PropTypes.shape({
        disponible: PropTypes.bool,
        tieneAdjunto: PropTypes.bool,
        tieneCuerpoMail: PropTypes.bool,
        previewEndpoint: PropTypes.string,
    }),
    historicoEmbargos: PropTypes.arrayOf(PropTypes.object),
    idEmail: PropTypes.number,
};

export default MatchingJudicialDetail;
